import EventBus from '@mvps-genesis/event-bus';
import ParameterValidator from '@mvps-genesis/parameter-validator';
import MicroModal from 'micromodal';
import LoadingUI from 'extensions/loading-ui';

/*
 *--------------------------------------------------------------------------
 * MiniBasket
 *--------------------------------------------------------------------------
 *
 * Class to handle mink basket functionality and interaction.
 *
 */
export default class MiniBasket {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	_validator;

	/**
	 * Modal object
	 *
	 * @var Object
	 */
	modal;

	/**
	 * Element selectors object
	 *
	 * @var Object
	 */
	elementSelectors;

	/**
	 * Mini basket container element
	 *
	 * @var Element
	 */
	containerElement;

	/**
	 * Mini basket content element
	 *
	 * @var Element
	 */
	contentElement;

	/**
	 * List of elements that open the mini basket and contain the mini basket trigger attribute
	 *
	 * @var NodeList
	 */
	openElements;

	/**
	 * Attribute to trigger mini basket open
	 *
	 * @var String
	 */
	openAttribute;

	/**
	 * List of basket count elements
	 *
	 * @var NodeList
	 */
	countElements;

	/**
	 * List of subtotal elements
	 *
	 * @var NodeList
	 */
	subtotalElements;

	/**
	 * Open mini basket on instantiation
	 *
	 * @var Boolean
	 */
	isOpenByDefault;

	/**
	 * Open mini basket after successfully adding product to cart
	 *
	 * @var Boolean
	 */
	openOnAddToCart;

	/**
	 * Close mini basket after opening on successful add to cart
	 *
	 * @var Boolean
	 */
	closeAfterOpen;

	/**
	 * Close mini basket delay time (ms)
	 *
	 * @var Integer
	 */
	closeAfterOpenTime;

	/**
	 * Mini basket has items class name
	 *
	 * @var String
	 */
	hasItemsClass;

	/**
	 * Loading UI functionality object
	 *
	 * @var {LoadingUI} extensions/loading-ui
	 */
	loadingUI;

	/**
	 * Create a new MiniBasket object
	 *
	 * @param	Boolean		closeAfterOpen		Close mini basket after opened via openOnAddToCart. Defaults to false.
	 * @param	Integer		closeAfterOpenTime	Close mini basket after delay (ms) if closeAfterOpen option is true. Defaults to 5000.
	 * @param	Element		containerElement	Root container element to target for mini basket functionality. Defaults to '.js-mini-basket'.
	 * @param	Element		contentElement		Element containing mini basket main content. Defaults to '.g-mini-basket__content'.
	 * @param	ElementList	countElements		List of basket count elements. Defaults to '.js-mini-basket-count'.
	 * @param	String		hasItemsClass		Class to apply to mini basket when items are in the basket. Defaults to 'g-site-header__basket-has-items'.
	 * @param	Boolean		isOpenByDefault		Open mini basket on class instantiation. Defaults to false.
	 * @param	Element		loadingElement		The target loading element. Defaults to '#js-mini-basket-loading-container'.
	 * @param	String		openAttribute		Attribute name to trigger mini basket open. This should be placed on the openElements. Defaults to 'data-modal-trigger'.
	 * @param	ElementList	openElements		List of elements that open the mini basket and contain the mini basket trigger attribute. Defaults to '.js-mini-basket-open'.
	 * @param	Boolean		openOnAddToCart		Listens for the global `MVPS:addedToCart` event and opens if successful. Defaults to false.
	 * @param	ElementList	subtotalElements	List of subtotal elements. Defaults to '.js-mini-basket-subtotal'.
	 */
	// eslint-disable-next-line max-lines-per-function
	constructor ({
		closeAfterOpen = false,
		closeAfterOpenTime = 5000,
		containerElement = '.js-mini-basket',
		contentElement = '.g-mini-basket__content',
		countElements = '.js-mini-basket-count',
		hasItemsClass = 'g-site-header__basket-has-items',
		isOpenByDefault = false,
		loadingElement = '#js-mini-basket-loading-container',
		openAttribute = 'data-modal-trigger',
		openElements = '.js-mini-basket-open',
		openOnAddToCart = false,
		subtotalElements = '.js-mini-basket-subtotal'
	} = {}) {
		// Create validator
		this._validator = new ParameterValidator(this);

		// Save selectors
		this.elementSelectors = {
			containerElement,
			contentElement,
			countElements,
			openElements,
			subtotalElements
		};

		// Create properties from options
		this.closeAfterOpenTime = this._validator.isInt('options.closeAfterOpenTime', closeAfterOpenTime);
		this.closeAfterOpen = this._validator.isBoolean('options.closeAfterOpen', closeAfterOpen);
		this.containerElement = this._validator.isElement('options.containerElement', containerElement);
		this.contentElement = this._validator.isElement('options.contentElement', contentElement);
		this.countElements = this._validator.isElementList('options.countElements', countElements);
		this.hasItemsClass = this._validator.isString('options.hasItemsClass', hasItemsClass);
		this.isOpenByDefault = this._validator.isBoolean('options.isOpenByDefault', isOpenByDefault);
		this.loadingElement = this._validator.isElement('options.loadingElement', loadingElement);
		this.openAttribute = this._validator.isString('options.openAttribute', openAttribute);
		this.openElements = this._validator.isElementList('options.openElements', openElements);
		this.openOnAddToCart = this._validator.isBoolean('options.openOnAddToCart', openOnAddToCart);
		this.subtotalElements = this._validator.isElementList('options.subtotalElements', subtotalElements);

		// Determine if user want reduced motion. If they do, no animations.
		const awaitAnimation = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

		// Set modal object
		this.modal = {};
		this.modal.id = this.containerElement.id || 'js-mini-basket';
		this.modal.options = {
			awaitCloseAnimation: awaitAnimation,
			awaitOpenAnimation: awaitAnimation,
			disableScroll: true,
			openTrigger: this.openAttribute
		};

		// Run bind events init function
		this.bindEvents();

		// Run open command if isOpenByDefault is true
		if (this.isOpenByDefault) {
			this.open();
		} else {
			MicroModal.init(this.modal.options);
		}
	}

	bindEvents () {
		// Listen for global event - `MVTPS:addToCart` and handle response
		EventBus.addEventListener('MVPS:addToCart', this.onAddToCart.bind(this), false);

		for (const element of this.openElements) {
			element.addEventListener('click', (event) => {
				event.preventDefault();
			});
		}

		this.bindInnerEvents();
	}

	bindInnerEvents () {
		this.setLoadingUI();

		const removeElements = this.containerElement.getElementsByClassName('js-mini-basket-group-remove-link');

		for (const removeElement of removeElements) {
			removeElement.addEventListener('click', this.removeEvent.bind(this));
		}
	}

	onAddToCart (e) {
		const payload = e.detail;

		if (payload.success) {
			this.onAddToCartSuccess(payload);
		}
		else {
			this.onAddToCartError(payload);
		}
	};

	onAddToCartSuccess (payload) {
		const newContainer = this.getContainerElementFromPayload(payload);

		if (!newContainer) {
			return;
		}

		// Replace mini basket contents with payload content
		this.replaceContents(newContainer);

		// Set count and subtotal
		this.count = this.getCount(newContainer);
		this.subtotal = this.getSubtotal(newContainer);

		// Update count / subtotal elements
		this.updateCounts();
		this.updateSubtotals();
		this.bindInnerEvents();

		// Open mini basket if configured
		if (this.openOnAddToCart) {
			setTimeout(() => {
				this.open();

				// Close mini basket after X ms if configured
				if (this.closeAfterOpen) {
					setTimeout(() => {
						MicroModal.close(this.modal.id);
					}, this.closeAfterOpenTime);
				}
			}, 200);
		}
	}

	onAddToCartError () {
		return false;
	}

	getContainerElementFromPayload (payload) {
		return payload.data.querySelector(this.elementSelectors.containerElement);
	}

	replaceContents (containerElement) {
		this._validator.isInstance('replaceContents.containerElement', containerElement, HTMLElement);

		const contentElement = containerElement.querySelector(this.elementSelectors.contentElement);

		if (!contentElement) {
			return;
		}

		this.contentElement.innerHTML = contentElement.innerHTML;
	}

	replaceContainerElement (newContainer) {
		this.containerElement.parentNode.replaceChild(newContainer, this.containerElement);

		this.containerElement = newContainer;
	}

	getCount (containerElement) {
		const element = containerElement ? containerElement : this.containerElement;

		return element.dataset.count;
	}

	getSubtotal (containerElement) {
		const element = containerElement ? containerElement : this.containerElement;

		return element.dataset.subtotal;
	}

	updateCounts () {
		for (const element of this.countElements) {
			element.textContent = this.count;
		}

		for (const openElement of this.openElements) {
			if (this.count) {
				openElement.classList.add(this.hasItemsClass);
			} else {
				openElement.classList.remove(this.hasItemsClass);
			}
		}
	}

	updateSubtotals () {
		for (const element of this.subtotalElements) {
			element.textContent = this.subtotal;
		}
	}

	open () {
		MicroModal.show(this.modal.id, this.modal.options);
	}

	setLoadingUI () {
		this.loadingUI = new LoadingUI(this.loadingElement, {
			overlayElement: this.contentElement ?? false
		});
	}

	async removeEvent (event) {
		if (window.mivaJS.Page_Code === 'BASK') {
			return true;
		}

		event.preventDefault();
		this.loadingUI.show();

		const response = await fetch(event.currentTarget.href);

		if (!response.ok) {
			this.loadingUI.hide();
			return this.onAddToCartError();
		}

		const responseHtml = await response.text();
		const responseDom = new DOMParser().parseFromString(responseHtml, 'text/html');

		this.onAddToCartSuccess({
			data: responseDom,
			success: 1
		});

		this.loadingUI.hide();

		return true;
	}

};
